import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useShallow } from "zustand/react/shallow";
import { useSelectionStore } from "../../stores/selectionStore";
import { PROPERTY_PANEL_CONFIG } from "../../utils/panelConfig";
import panelConfig from "../../utils/panelConfig";
import DraggablePanel from "../draggable-panels/DraggablePanel";
import InspectorHeaderButtons from "./InspectorHeaderButtons";
import InspectorMinimizedTab from "./InspectorMinimizedTab";
import InspectorPanelBody from "./InspectorPanelBody";
import InspectorPanelContent from "./InspectorPanelContent";
import InspectorPanelTitle from "./InspectorPanelTitle";
import { useInspectorDuplicateDelete } from "./useInspectorDuplicateDelete";
import { useInspectorPanelVisibility } from "./useInspectorPanelVisibility";
import { useInspectorQuickNav } from "./useInspectorQuickNav";
import { useInspectorReparent } from "./useInspectorReparent";
import { useInspectorSelectionRecords } from "./useInspectorSelectionRecords";
import { useInspectorTitleEditing } from "./useInspectorTitleEditing";
import {
  useBodies,
  useJoints,
  useMeshes,
  useWorldBodies,
  useSceneActions,
} from "../../stores/sceneStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import { SHORTCUT_ACTIONS } from "../../shortcuts/defaultBindings";
import { useShortcutAction } from "../../shortcuts/useShortcutAction";
import { useInspectorMobileSheetLayout } from "../../hooks/useInspectorMobileSheetLayout";
import { useIsMobile } from "../../hooks/useIsMobile";
import { logger } from "../../utils/logger";
import PinnedInspectorNav from "./PinnedInspectorNav";
import {
  getInspectorPanelTitle,
  InspectorPanelIcon,
  resolveInspectorActivePanel,
} from "./inspectorPanelIdentity";
import { useSelectedVisualMeshIds } from "../../visual-mesh-multi-selection";
import { resolveVisualMeshTransformTargets } from "../../utils/visualMeshTransformTargets";

const { getStandardPanelProps, getStandardPosition } = panelConfig;

const PropertiesPanelManager = () => {
  const PANEL_MARGIN = 20;
  const PANEL_WIDTH = PROPERTY_PANEL_CONFIG.dimensions.width;
  const inspectorMobileSheet = useInspectorMobileSheetLayout();
  const isMobile = useIsMobile();

  const initialPosition = useMemo(
    () => ({
      x: Math.max(PANEL_MARGIN, window.innerWidth - PANEL_WIDTH - PANEL_MARGIN),
      y: PANEL_MARGIN,
    }),
    [PANEL_WIDTH]
  );

  const {
    selectedJoint,
    selectedActuator,
    selectedBody,
    selectedBodies,
    selectedMesh,
    selectedSite,
    selectedGeom,
    selectedInertial,
    selectedEquality,
    selectedTendon,
    selectedCamera,
    selectedLight,
    lastSelectionSource,
    selectionSequence,
    clearAllSelections,
    setLastSelectionSource,
    selectCamera,
  } = useSelectionStore(
    useShallow((state) => ({
      selectedJoint: state.selectedJoint,
      selectedActuator: state.selectedActuator,
      selectedBody: state.selectedBody,
      selectedBodies: state.selectedBodies,
      selectedMesh: state.selectedMesh,
      selectedSite: state.selectedSite,
      selectedGeom: state.selectedGeom,
      selectedInertial: state.selectedInertial,
      selectedEquality: state.selectedEquality,
      selectedTendon: state.selectedTendon,
      selectedCamera: state.selectedCamera,
      selectedLight: state.selectedLight,
      lastSelectionSource: state.lastSelectionSource,
      selectionSequence: state.selectionSequence,
      clearAllSelections: state.clearAllSelections,
      setLastSelectionSource: state.setLastSelectionSource,
      selectCamera: state.selectCamera,
    }))
  );
  const isPhysicsRunning = usePhysicsStore((s) => s.isRunning);

  const {
    allEqualities,
    isSelectedBodyWorldRoot,
    missingSelection,
    resolvedActiveVisualGeom,
    resolvedSelectedGeom,
    resolvedSelectedMeshVisualGeom,
    selectedActuatorRecord,
    selectedBodyExists,
    selectedBodyName,
    selectedCameraRecord,
    selectedEqualityRecord,
    selectedGeomIsTendonWrap,
    selectedGeomIsVisual,
    selectedInertialBodyName,
    selectedJointRecord,
    selectedLightRecord,
    selectedSiteRecord,
    selectedTendonRecord,
    selectedWorldBodyDisplayName,
  } = useInspectorSelectionRecords({
    selectedActuator,
    selectedBody,
    selectedCamera,
    selectedEquality,
    selectedGeom,
    selectedInertial,
    selectedJoint,
    selectedLight,
    selectedMesh,
    selectedSite,
    selectedTendon,
  });
  const sceneActions = useSceneActions();
  const allBodies = useBodies();
  const allJoints = useJoints();
  const allMeshes = useMeshes();
  const allWorldBodies = useWorldBodies();
  const selectedVisualMeshIds = useSelectedVisualMeshIds();
  const selectedVisualMeshCount = selectedVisualMeshIds.size;
  const missingSelectionLogKeyRef = useRef(null);
  const selectedVisualMeshTransformState = useMemo(
    () => resolveVisualMeshTransformTargets(allBodies, selectedVisualMeshIds),
    [allBodies, selectedVisualMeshIds]
  );
  const [pendingVisualMeshParentBodyUuid, setPendingVisualMeshParentBodyUuid] = useState(null);
  const shouldShowObjectPanel =
    Boolean(selectedBody) &&
    ((isSelectedBodyWorldRoot &&
      (lastSelectionSource === "sceneTreeObject" || lastSelectionSource === "objectLibrary")) ||
      lastSelectionSource === "objectPropertiesLink");

  const activePanel = resolveInspectorActivePanel({
    pendingVisualMeshParentBodyUuid,
    resolvedSelectedMeshVisualGeom,
    selectedBodies,
    selectedBody,
    selectedCamera,
    selectedLight,
    selectedEquality,
    selectedGeom,
    selectedGeomIsTendonWrap,
    selectedGeomIsVisual,
    selectedInertial,
    selectedJoint,
    selectedJointRecord,
    selectedMesh,
    selectedSite,
    selectedTendon,
    selectedVisualMeshCount,
    shouldShowObjectPanel,
  });
  const getPanelIcon = useCallback(
    (className = "w-5 h-5") => (
      <InspectorPanelIcon activePanel={activePanel} className={className} />
    ),
    [activePanel]
  );

  useEffect(() => {
    if (!missingSelection) {
      missingSelectionLogKeyRef.current = null;
      return;
    }
    const logKey = `${missingSelection.kind}:${missingSelection.id}`;
    if (missingSelectionLogKeyRef.current === logKey) return;
    missingSelectionLogKeyRef.current = logKey;
    logger.warn(missingSelection.message, {
      selectionKind: missingSelection.kind,
      selectionId: missingSelection.id,
    });
  }, [missingSelection]);

  const {
    handleMinimize,
    inspectorChromeFlashRootClass,
    minimized,
    mobileScenePeekCompact,
    setMobileScenePeekExpanded,
    setMinimized,
    showMobilePeekSettingsFooter,
    tabAttentionAnimating,
  } = useInspectorPanelVisibility({
    activePanel,
    isMobile,
    lastSelectionSource,
    pendingVisualMeshParentBodyUuid,
    selectionSequence,
    selectedActuator,
    selectedBody,
    selectedCamera,
    selectedEquality,
    selectedGeom,
    selectedInertial,
    selectedJoint,
    selectedLight,
    selectedSite,
    selectedTendon,
    setPendingVisualMeshParentBodyUuid,
  });

  const {
    inspectorReparentConfig,
    inspectorReparentMenuPosition,
    inspectorReparentMenuRef,
    inspectorReparentOpen,
    inspectorReparentRef,
    setInspectorReparentMenuPosition,
    setInspectorReparentOpen,
  } = useInspectorReparent({
    activePanel,
    allBodies,
    allJoints,
    isPhysicsRunning,
    resolvedActiveVisualGeom,
    sceneActions,
    selectedBody,
    selectedBodyExists,
    selectedGeom,
    selectedInertial,
    selectedJoint,
    selectedSite,
    selectedSiteRecord,
  });

  useEffect(() => {
    if (!selectedMesh || selectedGeom || !resolvedSelectedMeshVisualGeom?.bodyUuid) return;
    const geomUuid = resolvedSelectedMeshVisualGeom.geom?.uuid;
    if (!geomUuid) return;
    useSelectionStore
      .getState()
      .selectGeom(`${resolvedSelectedMeshVisualGeom.bodyUuid}:${String(geomUuid)}`);
  }, [selectedGeom, selectedMesh, resolvedSelectedMeshVisualGeom]);

  const { quickNavBackTarget, quickNavForwardTarget, handleQuickNavBack, handleQuickNavForward } =
    useInspectorQuickNav({
      activePanel,
      allEqualities,
      pendingVisualMeshParentBodyUuid,
      resolvedActiveVisualGeom,
      selectedActuator,
      selectedActuatorRecord,
      selectedBody,
      selectedBodyName,
      selectedCamera,
      selectedCameraRecord,
      selectedEquality,
      selectedEqualityRecord,
      selectedGeom,
      selectedInertial,
      selectedInertialBodyName,
      selectedJoint,
      selectedJointRecord,
      selectedLight,
      selectedLightRecord,
      selectedMesh,
      selectedSite,
      selectedSiteRecord,
      selectedTendon,
      selectedTendonRecord,
      selectedWorldBodyDisplayName,
    });

  const {
    cancelTitleRename,
    commitTitleRename,
    isRenamingTitle,
    renameTarget,
    setTitleDraftName,
    startInspectorRename,
    titleDraftName,
  } = useInspectorTitleEditing({
    activePanel,
    resolvedSelectedGeom,
    sceneActions,
    selectedActuator,
    selectedActuatorRecord,
    selectedBody,
    selectedBodyExists,
    selectedBodyName,
    selectedCamera,
    selectedCameraRecord,
    selectedEquality,
    selectedEqualityRecord,
    selectedInertial,
    selectedInertialBodyName,
    selectedJoint,
    selectedJointRecord,
    selectedLight,
    selectedLightRecord,
    selectedSite,
    selectedSiteRecord,
    selectedTendon,
    selectedTendonRecord,
    selectedWorldBodyDisplayName,
  });

  const titleInfo = useMemo(
    () =>
      getInspectorPanelTitle({
        activePanel,
        allEqualities,
        allMeshes,
        pendingVisualMeshParentBodyUuid,
        resolvedActiveVisualGeom,
        resolvedSelectedGeom,
        selectedActuator,
        selectedActuatorRecord,
        selectedBodyName,
        selectedCamera,
        selectedCameraRecord,
        selectedEquality,
        selectedEqualityRecord,
        selectedInertialBodyName,
        selectedJoint,
        selectedJointRecord,
        selectedLight,
        selectedLightRecord,
        selectedSite,
        selectedSiteRecord,
        selectedTendon,
        selectedTendonRecord,
        selectedWorldBodyDisplayName,
      }),
    [
      activePanel,
      allEqualities,
      allMeshes,
      selectedEquality,
      selectedEqualityRecord,
      selectedTendon,
      selectedTendonRecord,
      selectedActuator,
      selectedActuatorRecord,
      selectedJoint,
      selectedJointRecord,
      selectedLight,
      selectedLightRecord,
      selectedCamera,
      selectedCameraRecord,
      selectedBodyName,
      selectedSite,
      selectedSiteRecord,
      selectedInertialBodyName,
      resolvedActiveVisualGeom,
      resolvedSelectedGeom,
      selectedWorldBodyDisplayName,
      pendingVisualMeshParentBodyUuid,
    ]
  );
  const {
    handleInspectorDelete,
    handleInspectorDuplicate,
    inspectorDeleteDisabled,
    inspectorDeleteLabel,
    inspectorDuplicateDisabled,
    showInspectorDuplicate,
  } = useInspectorDuplicateDelete({
    activePanel,
    clearAllSelections,
    isPhysicsRunning,
    pendingVisualMeshParentBodyUuid,
    resolvedSelectedGeom,
    sceneActions,
    selectedActuator,
    selectedBody,
    selectedBodyExists,
    selectedBodies,
    selectedCamera,
    selectedEquality,
    selectedEqualityRecord,
    selectedGeom,
    selectedInertial,
    selectedJoint,
    selectedJointRecord,
    selectedLight,
    selectedLightRecord,
    selectedSiteRecord,
    selectedTendon,
    selectedTendonRecord,
    selectedVisualMeshIds,
    setPendingVisualMeshParentBodyUuid,
  });

  const inspectorShortcutOptions = useMemo(
    () => ({
      preventDefault: true,
      priority: 15,
    }),
    []
  );
  const copiedDuplicateSelectionRef = useRef(null);

  const duplicateSelectionKey = useMemo(() => {
    if (inspectorDuplicateDisabled) return "";
    if (activePanel === "body" || activePanel === "object") {
      return selectedBody ? `${activePanel}:${selectedBody}` : "";
    }
    if (activePanel === "geom") return selectedGeom ? `geom:${selectedGeom}` : "";
    if (activePanel === "visualGeom") return selectedGeom ? `visualGeom:${selectedGeom}` : "";
    if (activePanel === "joint") return selectedJoint ? `joint:${selectedJoint}` : "";
    if (activePanel === "site") return selectedSite ? `site:${selectedSite}` : "";
    if (activePanel === "equality") return selectedEquality ? `equality:${selectedEquality}` : "";
    if (activePanel === "tendon") return selectedTendon ? `tendon:${selectedTendon}` : "";
    if (activePanel === "multiBody") {
      return selectedBodies.length > 1 ? `multiBody:${selectedBodies.join(",")}` : "";
    }
    if (activePanel === "multiMesh") {
      const meshIds = Array.from(selectedVisualMeshIds || []);
      return meshIds.length > 1 ? `multiMesh:${meshIds.join(",")}` : "";
    }
    return "";
  }, [
    activePanel,
    inspectorDuplicateDisabled,
    selectedBody,
    selectedBodies,
    selectedEquality,
    selectedGeom,
    selectedJoint,
    selectedSite,
    selectedTendon,
    selectedVisualMeshIds,
  ]);

  const handleInspectorCopySelection = useCallback(() => {
    if (!duplicateSelectionKey) return false;
    copiedDuplicateSelectionRef.current = duplicateSelectionKey;
    return true;
  }, [duplicateSelectionKey]);

  const handleInspectorPasteDuplicate = useCallback(() => {
    if (!duplicateSelectionKey || copiedDuplicateSelectionRef.current !== duplicateSelectionKey) {
      return false;
    }
    return handleInspectorDuplicate();
  }, [duplicateSelectionKey, handleInspectorDuplicate]);

  useShortcutAction(
    SHORTCUT_ACTIONS.INSPECTOR_RENAME,
    startInspectorRename,
    inspectorShortcutOptions
  );

  useShortcutAction(
    SHORTCUT_ACTIONS.INSPECTOR_COPY_SELECTION,
    handleInspectorCopySelection,
    inspectorShortcutOptions
  );

  useShortcutAction(
    SHORTCUT_ACTIONS.INSPECTOR_PASTE_DUPLICATE,
    handleInspectorPasteDuplicate,
    inspectorShortcutOptions
  );

  useShortcutAction(
    SHORTCUT_ACTIONS.INSPECTOR_DUPLICATE,
    handleInspectorDuplicate,
    inspectorShortcutOptions
  );

  const title = useMemo(
    () => (
      <InspectorPanelTitle
        activePanel={activePanel}
        allBodies={allBodies}
        allWorldBodies={allWorldBodies}
        cancelTitleRename={cancelTitleRename}
        commitTitleRename={commitTitleRename}
        handleInspectorDelete={handleInspectorDelete}
        handleInspectorDuplicate={handleInspectorDuplicate}
        inspectorDeleteDisabled={inspectorDeleteDisabled}
        inspectorDeleteLabel={inspectorDeleteLabel}
        inspectorDuplicateDisabled={inspectorDuplicateDisabled}
        inspectorReparentConfig={inspectorReparentConfig}
        inspectorReparentMenuPosition={inspectorReparentMenuPosition}
        inspectorReparentMenuRef={inspectorReparentMenuRef}
        inspectorReparentOpen={inspectorReparentOpen}
        inspectorReparentRef={inspectorReparentRef}
        isMobile={isMobile}
        isPhysicsRunning={isPhysicsRunning}
        isRenamingTitle={isRenamingTitle}
        mobileScenePeekCompact={mobileScenePeekCompact}
        renameTarget={renameTarget}
        resolvedSelectedGeom={resolvedSelectedGeom}
        sceneActions={sceneActions}
        selectCamera={selectCamera}
        selectedCamera={selectedCamera}
        selectedVisualMeshTransformState={selectedVisualMeshTransformState}
        setInspectorReparentMenuPosition={setInspectorReparentMenuPosition}
        setInspectorReparentOpen={setInspectorReparentOpen}
        setLastSelectionSource={setLastSelectionSource}
        setMobileScenePeekExpanded={setMobileScenePeekExpanded}
        setTitleDraftName={setTitleDraftName}
        showInspectorDuplicate={showInspectorDuplicate}
        startInspectorRename={startInspectorRename}
        titleDraftName={titleDraftName}
        titleInfo={titleInfo}
        missingSelection={missingSelection}
      />
    ),
    [
      activePanel,
      allBodies,
      allWorldBodies,
      cancelTitleRename,
      commitTitleRename,
      handleInspectorDelete,
      handleInspectorDuplicate,
      inspectorDeleteDisabled,
      inspectorDeleteLabel,
      inspectorDuplicateDisabled,
      inspectorReparentConfig,
      inspectorReparentMenuPosition,
      inspectorReparentMenuRef,
      inspectorReparentOpen,
      inspectorReparentRef,
      isMobile,
      isPhysicsRunning,
      isRenamingTitle,
      mobileScenePeekCompact,
      renameTarget,
      resolvedSelectedGeom,
      sceneActions,
      selectCamera,
      selectedCamera,
      selectedVisualMeshTransformState,
      setInspectorReparentMenuPosition,
      setInspectorReparentOpen,
      setLastSelectionSource,
      setMobileScenePeekExpanded,
      setTitleDraftName,
      showInspectorDuplicate,
      startInspectorRename,
      titleDraftName,
      titleInfo,
      missingSelection,
    ]
  );
  const headerButtons = (
    <InspectorHeaderButtons handleMinimize={handleMinimize} isMobile={isMobile} />
  );
  const pinnedInspectorNav =
    !minimized &&
    activePanel &&
    !missingSelection &&
    (!showMobilePeekSettingsFooter || mobileScenePeekCompact) ? (
      <PinnedInspectorNav
        backTarget={quickNavBackTarget}
        onBack={handleQuickNavBack}
        forwardTarget={quickNavForwardTarget}
        onForward={handleQuickNavForward}
        activePanel={activePanel}
      />
    ) : null;

  return (
    <>
      <InspectorMinimizedTab
        activePanel={activePanel}
        getPanelIcon={getPanelIcon}
        isMobile={isMobile}
        minimized={minimized}
        panelMargin={PANEL_MARGIN}
        setMinimized={setMinimized}
        tabAttentionAnimating={tabAttentionAnimating}
        titleInfo={titleInfo}
      />
      <DraggablePanel
        open={true}
        allowRootOverflowVisible
        propertiesPanelRoot
        rootExtraClassName={[
          "inspector-draggable-panel",
          inspectorMobileSheet ? "inspector-draggable-panel--mobile-sheet" : "",
          inspectorChromeFlashRootClass,
        ]
          .filter(Boolean)
          .join(" ")}
        onClose={handleMinimize}
        title={title}
        initialPosition={getStandardPosition(initialPosition)}
        minimized={minimized}
        minimizedTargetPosition={{ x: window.innerWidth - 24, y: PANEL_MARGIN + 20 }}
        smoothMainMenuStyleSlide
        keepRightAnchoredUntilMoved
        minimizedExitToLeft={inspectorMobileSheet}
        showHeaderBar={false}
        rightButton={headerButtons}
        bottomOutsideContent={pinnedInspectorNav}
        {...getStandardPanelProps()}
      >
        <InspectorPanelBody
          minimized={minimized}
          mobileScenePeekCompact={mobileScenePeekCompact}
          setMobileScenePeekExpanded={setMobileScenePeekExpanded}
          showMobilePeekSettingsFooter={showMobilePeekSettingsFooter && Boolean(activePanel)}
        >
          <InspectorPanelContent
            activePanel={activePanel}
            handleQuickNavBack={handleQuickNavBack}
            handleQuickNavForward={handleQuickNavForward}
            pendingVisualMeshParentBodyUuid={pendingVisualMeshParentBodyUuid}
            quickNavBackTarget={quickNavBackTarget}
            quickNavForwardTarget={quickNavForwardTarget}
            setPendingVisualMeshParentBodyUuid={setPendingVisualMeshParentBodyUuid}
            missingSelection={missingSelection}
          />
        </InspectorPanelBody>
      </DraggablePanel>
    </>
  );
};

export default PropertiesPanelManager;
